/**
 * Type definitions for Cold Outreach Email Campaign Templates
 * Based on best practices from HubSpot, Outreach, Salesloft, Apollo.io, Gong, ZoomInfo
 *
 * Key insights from billion-dollar cold outreach companies:
 * - Lead with the prospect's pain, not your product
 * - Personalization increases reply rates 2-3x
 * - 3-5 email sequences perform best
 * - Tuesday-Thursday 8-10 AM local time optimal
 * - Subject lines under 40 chars get higher opens
 * - Plain-text appearance outperforms heavy HTML
 */

export type CampaignCategory =
  | "prospecting"
  | "followup"
  | "meeting"
  | "event"
  | "abm"
  | "reengagement"
  | "industry"
  | "partner";

export type TargetAudience =
  | "b2b-saas"
  | "agency"
  | "enterprise"
  | "ecommerce"
  | "startup"
  | "generic";

export type PersonalizationLevel = "basic" | "moderate" | "high" | "hyper";

export type LeadSource =
  | "linkedin"
  | "website"
  | "referral"
  | "event"
  | "purchased"
  | "scraped"
  | "manual"
  | "crm";

export type CompanySize =
  | "1-10"
  | "11-50"
  | "51-200"
  | "201-500"
  | "501-1000"
  | "1000+";

export interface CampaignEmailStep {
  /** Display name for the email step (e.g., "Day 0 - Problem Hook") */
  name: string;
  /** Email subject line with variable placeholders */
  subject: string;
  /** ID of the matching MJML template from coldOutreach templates */
  suggestedMjmlTemplateId: string;
  /** Delay after previous email in the sequence */
  delayAfterPrevious: {
    value: number;
    unit: "minutes" | "hours" | "days";
  };
  /** Purpose/goal of this specific email */
  purpose: string;
  /** Suggested send time for optimal open rates */
  optimalSendTime?: {
    dayOfWeek: ("monday" | "tuesday" | "wednesday" | "thursday" | "friday")[];
    timeSlot: "morning" | "midday" | "afternoon";
  };
  /** Variables specific to this email step */
  variables?: Array<{
    key: string;
    defaultValue: string;
    description?: string;
    required?: boolean;
  }>;
  /** A/B testing suggestions for this email */
  abTestSuggestions?: {
    subjectVariants?: string[];
    ctaVariants?: string[];
  };
}

export interface CampaignTemplate {
  /** Unique identifier (kebab-case) */
  id: string;
  /** Display name */
  name: string;
  /** Campaign category */
  category: CampaignCategory;
  /** Short description (1-2 sentences) */
  description: string;
  /** Detailed strategy explanation with industry references */
  strategy: string;
  /** Icon identifier (Ant Design icon name) */
  icon: string;
  /** Primary color for UI (hex) */
  color: string;
  /** Target audience type */
  targetAudience: TargetAudience;
  /** Level of personalization required */
  personalizationLevel: PersonalizationLevel;

  /** Expected outcome metrics */
  expectedOutcomes: string[];
  /** Best practices from billion-dollar companies */
  bestPractices: string[];
  /** Common objections and how to handle them */
  objectionHandling?: string[];

  /** Default segmentation for cold outreach */
  segmentation: {
    /** Source of leads (how they were acquired) */
    leadSource: LeadSource[];
    /** Company size targeting */
    companySize: CompanySize[];
    /** Industry verticals */
    industries?: string[];
    /** Job title patterns */
    jobTitles?: string[];
    /** Minimum data requirements for this campaign */
    requiredFields: string[];
  };

  /** Email sequence configuration */
  emails: CampaignEmailStep[];

  /** Sending configuration recommendations */
  sendingConfig: {
    /** Recommended emails per day per sender */
    emailsPerDayPerSender: number;
    /** Recommended daily limit total */
    dailyLimit: number;
    /** Whether email domain warming is recommended */
    requiresWarming: boolean;
    /** Minimum domain age in days (for deliverability) */
    minDomainAge?: number;
  };

  /** Campaign-level variables with smart defaults */
  variables: Array<{
    key: string;
    defaultValue: string;
    description: string;
    source?: "crm" | "enrichment" | "manual";
  }>;

  /** Tags for filtering/searching */
  tags: string[];
  /** Industry/use case this template is best for */
  bestFor: string[];
  /** Difficulty level for setup */
  difficulty: "beginner" | "intermediate" | "advanced";
  /** Estimated setup time */
  setupTime: string;
  /** Companies/frameworks this template is inspired by */
  inspiredBy?: string[];
}

export interface CampaignTemplateCategory {
  id: CampaignCategory;
  name: string;
  description: string;
  icon: string;
  color: string;
  templates: CampaignTemplate[];
}

/**
 * Standard variables available in all cold outreach templates
 */
export const STANDARD_VARIABLES = {
  // Recipient info
  first_name: "Recipient's first name",
  last_name: "Recipient's last name",
  full_name: "Recipient's full name",
  email: "Recipient's email address",
  job_title: "Recipient's job title",
  company_name: "Recipient's company name",
  company_size: "Company employee count",
  industry: "Company industry",

  // Sender info
  sender_name: "Sales rep's name",
  sender_title: "Sales rep's title",
  sender_company: "Your company name",
  sender_phone: "Sales rep's phone",
  sender_linkedin: "Sales rep's LinkedIn URL",
  sender_photo_url: "Sales rep's photo URL",

  // Personalization
  pain_point: "Identified pain point",
  value_prop: "Value proposition",
  personalized_opener: "Custom opening line",
  trigger_event: "Trigger that prompted outreach",
  mutual_connection: "Shared connection or interest",
  recent_news: "Recent company news/trigger",

  // Social proof
  case_study_company: "Reference customer name",
  case_study_result: "Quantified result",
  testimonial_quote: "Customer testimonial",
  customer_logo_url: "Customer logo image",

  // CTAs
  calendar_link: "Calendly/booking link",
  asset_url: "Resource/content URL",
  demo_url: "Demo scheduling link",
  video_url: "Personalized video URL",

  // Compliance
  unsubscribe_url: "Unsubscribe link",
  company_address: "Physical address for CAN-SPAM",
} as const;

/**
 * Optimal send times based on cold email research
 */
export const OPTIMAL_SEND_TIMES = {
  morning: { start: 8, end: 10, label: "8-10 AM" },
  midday: { start: 11, end: 13, label: "11 AM - 1 PM" },
  afternoon: { start: 14, end: 16, label: "2-4 PM" },
} as const;

/**
 * Category metadata for UI display
 */
export const CATEGORY_METADATA: Record<
  CampaignCategory,
  { name: string; icon: string; color: string; description: string }
> = {
  prospecting: {
    name: "Prospecting",
    icon: "AimOutlined",
    color: "#3b82f6",
    description: "Initial cold outreach to new prospects",
  },
  followup: {
    name: "Follow-Up",
    icon: "ReloadOutlined",
    color: "#10b981",
    description: "Nurture sequences after initial contact",
  },
  meeting: {
    name: "Meeting Requests",
    icon: "CalendarOutlined",
    color: "#8b5cf6",
    description: "Book demos and discovery calls",
  },
  event: {
    name: "Event-Based",
    icon: "NotificationOutlined",
    color: "#f59e0b",
    description: "Webinars, conferences, and content offers",
  },
  abm: {
    name: "Account-Based",
    icon: "BankOutlined",
    color: "#ec4899",
    description: "Enterprise and strategic account outreach",
  },
  reengagement: {
    name: "Re-engagement",
    icon: "UndoOutlined",
    color: "#ef4444",
    description: "Revive lost deals and dormant leads",
  },
  industry: {
    name: "Industry-Specific",
    icon: "ShopOutlined",
    color: "#06b6d4",
    description: "Tailored for specific verticals",
  },
  partner: {
    name: "Partner Outreach",
    icon: "TeamOutlined",
    color: "#6366f1",
    description: "Agency and reseller recruitment",
  },
};
